import { useState } from "react";
import { Megaphone, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { JourneyAcquisitionEntryGroup } from "@/lib/api";

export type EntryGroupsSplitMode = "new" | "all";

type Props = {
  paid?: JourneyAcquisitionEntryGroup;
  other?: JourneyAcquisitionEntryGroup;
  paidCount?: number;
  otherCount?: number;
  title?: string;
  subtitle?: string;
  showModeToggle?: boolean;
  defaultMode?: EntryGroupsSplitMode;
  className?: string;
};

function countsForMode(
  paid: JourneyAcquisitionEntryGroup | undefined,
  other: JourneyAcquisitionEntryGroup | undefined,
  mode: EntryGroupsSplitMode,
  fallbackPaid?: number,
  fallbackOther?: number,
) {
  const paidN =
    mode === "new"
      ? paid?.new_sessions ?? fallbackPaid ?? 0
      : paid?.total_sessions ?? fallbackPaid ?? 0;
  const otherN =
    mode === "new"
      ? other?.new_sessions ?? fallbackOther ?? 0
      : other?.total_sessions ?? fallbackOther ?? 0;
  return { paidN, otherN, total: paidN + otherN };
}

export default function EntryGroupsSplit({
  paid,
  other,
  paidCount,
  otherCount,
  title = "Wejścia: płatne vs pozostałe",
  subtitle = "?via=ad lub Social płatny · reszta = organiczne, direct, wyszukiwarka itd.",
  showModeToggle = true,
  defaultMode = "all",
  className,
}: Props) {
  const [mode, setMode] = useState<EntryGroupsSplitMode>(defaultMode);
  const { paidN, otherN, total } = countsForMode(paid, other, mode, paidCount, otherCount);

  if (total <= 0) return null;

  const paidPct = total > 0 ? Math.round((paidN / total) * 1000) / 10 : 0;
  const otherPct = total > 0 ? Math.round((otherN / total) * 1000) / 10 : 0;

  return (
    <Card className={cn("border-2 border-brand/20", className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              {title}
              <InfoTip tipId="paid_entries" />
            </CardTitle>
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {showModeToggle && paid && other && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={mode === "new" ? "default" : "outline"}
                onClick={() => setMode("new")}
              >
                Nowe wizyty
              </Button>
              <Button
                size="sm"
                variant={mode === "all" ? "default" : "outline"}
                onClick={() => setMode("all")}
              >
                Wszystkie wizyty
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="flex h-10 overflow-hidden rounded-lg text-xs font-semibold text-white shadow-inner">
            {paidN > 0 && (
              <div
                className="flex min-w-[3rem] items-center justify-center bg-amber-500 px-2"
                style={{ width: `${Math.max(paidPct, 8)}%` }}
                title={`Płatne: ${paidN}`}
              >
                {paidPct >= 12 ? `${paidPct}% płatne` : ""}
              </div>
            )}
            {otherN > 0 && (
              <div
                className="flex min-w-[3rem] flex-1 items-center justify-center bg-slate-400 px-2"
                title={`Pozostałe: ${otherN}`}
              >
                {otherPct >= 12 ? `${otherPct}% pozostałe` : ""}
              </div>
            )}
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
            <span>Płatne {paidPct}%</span>
            <span>Pozostałe {otherPct}%</span>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <EntryBlock
            icon={Megaphone}
            label="Płatne wejścia"
            count={paidN}
            share={paidPct}
            tone="paid"
            hint="Reklama (?via=ad) lub UTM płatny"
          />
          <EntryBlock
            icon={Users}
            label="Pozostałe"
            count={otherN}
            share={otherPct}
            tone="other"
            hint="Organiczny social, direct, SEO…"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function EntryBlock({
  icon: Icon,
  label,
  count,
  share,
  tone,
  hint,
}: {
  icon: typeof Megaphone;
  label: string;
  count: number;
  share: number;
  tone: "paid" | "other";
  hint: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border-2 p-4",
        tone === "paid"
          ? "border-amber-400/60 bg-amber-50 dark:bg-amber-950/20"
          : "border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-900/40",
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon
          className={cn("h-5 w-5", tone === "paid" ? "text-amber-600" : "text-slate-500")}
        />
        {label}
      </div>
      <div
        className={cn(
          "mt-2 text-4xl font-bold tabular-nums tracking-tight",
          tone === "paid" ? "text-amber-700 dark:text-amber-400" : "text-slate-700 dark:text-slate-200",
        )}
      >
        {count}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">
        {share}% w oknie · {hint}
      </div>
    </div>
  );
}

export function EntryGroupChannelList({
  group,
  mode,
  title,
}: {
  group: JourneyAcquisitionEntryGroup;
  mode: EntryGroupsSplitMode;
  title: string;
}) {
  const rows = mode === "new" ? group.new_visits ?? [] : group.all_visits ?? [];
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.sessions ?? 0), 1);

  return (
    <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
      <div className="text-sm font-semibold">{title}</div>
      {rows.map((row) => {
        const n = row.sessions ?? 0;
        return (
          <div key={`${title}-${row.channel}`} className="grid gap-0.5">
            <div className="flex justify-between text-sm">
              <span>{row.label}</span>
              <span className="font-medium tabular-nums">{n}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-brand/70"
                style={{ width: `${Math.max(4, (n / max) * 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
